import { parseDbtCommit } from "../types/transformations";
import type { RunStepRow } from "../types/runs";

/**
 * #1664: what a run's transform step says it built. `ref` is the branch, tag or commit the
 * runner asked the image to clone (`metrics.dbtRef`, written at launch, so a failed clone has it
 * too). Below `instance.binding.read` the server replaces it with `dbtRefWithheld: true` (#1362),
 * so `refWithheld` tells a hidden branch from one never written. `commit` is the 40-hex commit
 * the image reached (`metrics.commit`), open to every reader. `builtIn` is a run of the built-in
 * project, which has no repository and so no ref.
 */
export interface DbtSource {
  ref: string | null;
  refWithheld: boolean;
  builtIn: boolean;
  commit: string | null;
}

const SHA_RE = /^[0-9a-f]{40}$/i;

/**
 * The run-level transform step's source, or null when there is nothing true to say: no transform
 * step, one that did not begin, or one that ended without launching dbt (a selector refusal, a
 * cycle, the dbt identity not ready, a cancel while queued) or is running and has not written
 * its launch metrics yet. A succeeded step with nothing recorded is an older run.
 */
export function dbtSourceOf(steps: readonly RunStepRow[]): DbtSource | null {
  const step = steps.find((row) => row.step === "transform" && row.dataflowId === null);
  if (!step || step.status === "pending" || step.status === "skipped") return null;
  const metrics =
    step.metrics !== null && typeof step.metrics === "object"
      ? (step.metrics as Record<string, unknown>)
      : {};
  const ref =
    typeof metrics.dbtRef === "string" && metrics.dbtRef.length > 0 ? metrics.dbtRef : null;
  const refWithheld = metrics.dbtRefWithheld === true;
  const commit = parseDbtCommit(metrics.commit);
  const builtIn = ref === null && !refWithheld && "dbtRef" in metrics;
  if (ref === null && !refWithheld && !builtIn && commit === null) {
    return step.status === "succeeded" ? { ref, refWithheld, builtIn, commit } : null;
  }
  return { ref, refWithheld, builtIn, commit };
}

/** The line for a ref the reader may see: a sha ref reads as the commit once. */
function refLine(ref: string, commit: string | null): string {
  const isSha = SHA_RE.test(ref);
  if (isSha && commit) return `dbt: ${commit}`;
  const term = isSha ? "Tag or commit" : "Branch";
  return commit ? `dbt: ${term} ${ref}, ${commit}` : `dbt: ${term} ${ref}`;
}

/** The line for a ref the server withheld: set, never "not recorded". */
function withheldLine(commit: string | null): string {
  const ask = "Ask an instance admin about the dbt repository.";
  return commit ? `dbt: ${commit}. Branch set. ${ask}` : `dbt: Set. ${ask}`;
}

/** "Branch main, Commit a1b2c3d", the parts known, or "not recorded" for an older run. */
export function dbtSourceLine(source: DbtSource): string {
  const commit = source.commit ? `Commit ${source.commit.slice(0, 7)}` : null;
  if (source.ref) return refLine(source.ref, commit);
  if (source.refWithheld) return withheldLine(commit);
  if (source.builtIn) return commit ? `dbt: Built-in project, ${commit}` : "dbt: Built-in project";
  return commit ? `dbt: ${commit}` : "dbt: not recorded";
}
